// UserDetailPanelFormazione.jsx - Dettaglio corsi per singolo utente

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, IconButton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Paper, Alert, Divider
} from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { cicassService } from '../../../../_services';

// Determina lo stato di ogni iscrizione
const getStatus = (item) => {
  const isPast = new Date(item.start) < new Date();
  if (item.deletedAt != null) return 'cancelled';
  if (!isPast) return 'enrolled';
  if (item.isPresente === 1) return 'present';
  return 'absent';
};

const STATUS_CONFIG = {
  present:   { label: 'Presente',  color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  absent:    { label: 'Assente',   color: 'error',   icon: <CancelIcon sx={{ fontSize: 14 }} /> },
  cancelled: { label: 'Cancellato',color: 'default', icon: <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} /> },
  enrolled:  { label: 'Iscritto',  color: 'primary', icon: <EventAvailableIcon sx={{ fontSize: 14 }} /> },
};

const TIPO_BADGE = {
  'SSE':    { bg: '#E2E8F0', border: '#94A3B8', color: '#1E293B' },
  'SSE+TS': { bg: '#E0F2FE', border: '#7DD3FC', color: '#0369A1' },
  'TS':     { bg: '#FEF3C7', border: '#FCD34D', color: '#92400E' },
};

const KpiCard = ({ value, label, color = '#2c5282', bgColor = '#e3f2fd' }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 90,
      textAlign: 'center',
      p: 1.5,
      borderRadius: 2,
      bgcolor: bgColor,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 'bold', color, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
      {label}
    </Typography>
  </Paper>
);

const UserDetailPanelFormazione = ({ user, year, apiUrl, onBack }) => {
  const [detail, setDetail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError(null);

    cicassService
      .getAll(`${apiUrl}/utente/dettaglio/${user.anaUserId}/${year}`)
      .then((res) => {
        const data = Array.isArray(res) ? res : res.data || [];
        // Ordina per data decrescente (più recente prima)
        data.sort((a, b) => new Date(b.start) - new Date(a.start));
        setDetail(data);
      })
      .catch((err) => {
        console.error('Errore caricamento dettaglio utente:', err);
        setError('Impossibile caricare il dettaglio. Riprova.');
      })
      .finally(() => setLoading(false));
  }, [user, year, apiUrl]);

  // KPI calcolati lato frontend dai dati ricevuti
  const kpi = detail.reduce(
    (acc, item) => {
      const status = getStatus(item);
      if (status === 'present')   { acc.present++;   acc.ore += item.durata || 0; }
      if (status === 'absent')    { acc.absent++;  }
      if (status === 'cancelled') { acc.cancelled++; }
      if (status === 'enrolled')  { acc.enrolled++;  }
      return acc;
    },
    { present: 0, absent: 0, cancelled: 0, enrolled: 0, ore: 0 }
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' , minHeight: 0 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 1.5 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">Caricamento...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* KPI Cards */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 1fr' },
            gap: 1,
            p: 1
            }}>
            <KpiCard value={kpi.ore}       label="Ore fatte"   color="#1565c0" bgColor="#e3f2fd" />
            <KpiCard value={kpi.present}   label="Presenze"    color="#2e7d32" bgColor="#e8f5e9" />
            <KpiCard value={kpi.absent}    label="Assenze"     color="#c62828" bgColor="#ffebee" />
            <KpiCard value={kpi.cancelled} label="Cancellati"  color="#555"    bgColor="#f5f5f5" />
            {kpi.enrolled > 0 && (
              <KpiCard value={kpi.enrolled} label="Iscritto"   color="#1565c0" bgColor="#e3f2fd" />
            )}
          </Box>

          <Divider />

          {/* Tabella dettaglio */}
          {detail.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Nessun corso trovato per {user.name} nell'anno {year}.
            </Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa', whiteSpace: 'nowrap' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Corso</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.map((item, idx) => {
                    const status = getStatus(item);
                    const cfg = STATUS_CONFIG[status];
                    const tipoBadge = TIPO_BADGE[item.tipoLezione] || TIPO_BADGE['SSE'];
                    const startDate = new Date(item.start);
                    const isCancelled = status === 'cancelled';

                    return (
                      <TableRow
                        key={item.formazioneId ?? idx}
                        sx={{
                          opacity: isCancelled ? 0.55 : 1,
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {startDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {startDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'medium',
                              textDecoration: isCancelled ? 'line-through' : 'none',
                              color: isCancelled ? 'text.secondary' : 'text.primary',
                            }}
                          >
                            {item.descrizione}
                          </Typography>
                          {item.istruttore1Name && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.istruttore1Name}
                              {item.istruttore2Name ? ` + ${item.istruttore2Name}` : ''}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                            <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                                {item.durata}h
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
                            <Box sx={{
                                display: 'inline-block',
                                px: 0.8, py: 0.1,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                borderRadius: '4px',
                                bgcolor: tipoBadge.bg,
                                border: `1px solid ${tipoBadge.border}`,
                                color: tipoBadge.color,
                            }}>
                                {item.tipoLezione}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
                            <Chip
                                icon={cfg.icon}
                                label={cfg.label}
                                color={cfg.color}
                                size="small"
                                variant={status === 'cancelled' ? 'outlined' : 'filled'}
                                sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                           </Box>
                        </TableCell>

                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default UserDetailPanelFormazione;